import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import { siteMeta, categories } from '../../mocks/site';
import { useDocMeta } from '../../hooks/useDocMeta';

const FORM_URL = 'https://readdy.ai/api/form/daeota9tto0ceqqnh1s0';

const principles = [
  {
    icon: 'ri-focus-2-line',
    title: '回到第一现场',
    desc: '每一条旧瓜都尽可能溯源到最初的公开出处，绝不以讹传讹。',
  },
  {
    icon: 'ri-time-line',
    title: '放进时间线里',
    desc: '孤立的一张贴图说明不了什么，把线索放回当年的语境里才能读懂真相。',
  },
  {
    icon: 'ri-divide-line',
    title: '分清事实与推测',
    desc: '公开事实、当事人说法与网友推测，我们永远分栏标注，从不明知故犯。',
  },
  {
    icon: 'ri-heart-line',
    title: '克制与尊重',
    desc: '重读旧闻不为伤害任何人，只为留下更接近完整的记录。',
  },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const setMeta = useDocMeta(
    `关于我们｜故纸瓜档`,
    '了解故纸瓜档的定位、发展理念与内容原则，并获取投稿爆料与联系渠道。我们专注娱乐圈陈年旧瓜、历史爆料的考古重读与当年真相还原。',
    '八卦爆料,故纸瓜档关于我们,八卦爆料考古重读,八卦爆料投稿',
  );

  useEffect(() => {
    setMeta();
    window.scrollTo(0, 0);
  }, [setMeta]);

  const [formState, setFormState] = useState<'idle' | 'sending' | 'success'>('idle');
  const [formError, setFormError] = useState<string>('');
  const [topic, setTopic] = useState('考古线索补充');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    for (const [k, v] of data.entries()) {
      if (k === 'website_alt') continue;
      if (v !== '' && typeof v === 'string') body.append(k, v);
    }

    setFormState('sending');
    setFormError('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg =
        (parsed?.meta?.message) || (parsed?.message) || (parsed?.meta?.detail) || '';
      const isSpam = String(responseText).toLowerCase().includes('spam');

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setFormState('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试或改用邮件联系。');
        setFormState('idle');
      }
    } catch {
      setFormState('idle');
      setFormError('网络异常，未能提交。请稍后重试，或直接发送邮件给我们。');
    }
  }

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们｜${siteMeta.name}`,
    description: siteMeta.description,
    mainEntity: {
      '@type': 'Organization',
      name: siteMeta.name,
      foundingDate: siteMeta.foundedYear,
      email: siteMeta.email,
    },
  };

  return (
    <PageLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />

      {/* 页头 */}
      <section className="relative flex min-h-[320px] w-full items-center overflow-hidden md:min-h-[380px]">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20editorial%20magazine%20newsroom%20desk%20with%20stacked%20archival%20files%20and%20a%20vintage%20typewriter%20under%20amber%20desk%20lamp%2C%20dusty%20moody%20atmosphere%2C%20documentary%20photography%2C%20deep%20maroon%20and%20brown%20tones%2C%20no%20text&width=1920&height=900&seq=gossip-about-bg&orientation=landscape"
          alt="故纸瓜档编辑部背景"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/50 to-foreground-950/30"></div>
        <div className="container-x relative z-10 py-20 pt-28 text-center md:pt-32">
          <p className="text-sm font-medium tracking-widest text-accent-300">ABOUT US</p>
          <h1 className="mt-3 font-display text-3xl font-black text-background-50 md:text-5xl">关于故纸瓜档</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-background-50/85 md:text-base">
            一家坚持「旧事重提、考古重读」的娱乐内容档案站，把散落在时间里的陈年旧瓜重新拼回完整的样子。
          </p>
        </div>
      </section>

      {/* 定位 */}
      <section className="bg-background-100">
        <div className="container-x grid grid-cols-1 gap-10 py-14 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-primary-700">WHO WE ARE</p>
            <h2 className="mt-2 font-display text-2xl font-black text-foreground-900 md:text-3xl">
              我们是谁，又为何做「八卦考古」
            </h2>
            <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
              成立于 {siteMeta.foundedYear} 年的{siteMeta.name}，最初只是几个旧刊爱好者在论坛里的考古帖。
              我们翻遍了旧报纸、旧访谈和泛黄的老照片，把一条条尘封往事重新拼接，意外发现很多当年的八卦，
              今天读来竟有全新的味道。
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              于是我们决定把这些重读结果归档成站，专注娱乐圈陈年旧瓜与历史爆料的考古重读。
              我们不追最新热点，只愿意慢下来，把「当年真相」讲得清楚、有据、有始有终。
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              对读者而言，这里是吃瓜考古的档案室；对我们而言，这是一份持续了九年的内容志业。
            </p>

            <div className="mt-7 grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-eye-line', label: '累计重读档案', value: '1800+ 篇' },
                { icon: 'ri-user-heart-line', label: '活跃考古同好', value: '26w+' },
                { icon: 'ri-calendar-check-line', label: '持续运营', value: `${new Date().getFullYear() - siteMeta.foundedYear} 年` },
                { icon: 'ri-archive-drawer-line', label: '栏目矩阵', value: `${categories.length} 大栏目` },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <div className="flex items-center gap-2 text-accent-600"><i className={s.icon}></i><span className="text-xs text-foreground-500">{s.label}</span></div>
                  <p className="mt-2 font-display text-xl font-black text-foreground-900">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold text-foreground-900">
                <i className="ri-compass-3-line text-accent-600"></i>五大栏目定位
              </h3>
              <div className="mt-5 flex flex-col gap-4">
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => navigate(`/column/${c.slug}`)}
                    className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-4 text-left transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                      <i className="ri-archive-line"></i>
                    </span>
                    <div className="min-w-0">
                      <h4 className="flex items-center justify-between font-display text-base font-bold text-foreground-900">
                        {c.name}
                        <span className="text-xs font-normal text-foreground-500">{c.count} 篇</span>
                      </h4>
                      <p className="mt-1 text-xs leading-5 text-foreground-600">{c.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="bg-background-50">
        <div className="container-x py-14 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-medium tracking-widest text-secondary-700">OUR PRINCIPLES</p>
            <h2 className="mt-2 font-display text-2xl font-black text-foreground-900 md:text-3xl">我们的内容原则</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                  <i className={p.icon}></i>
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-foreground-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 投稿联系 */}
      <section id="tougao" className="scroll-mt-20 bg-background-200">
        <div className="container-x py-14 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="text-sm font-medium tracking-wide text-primary-700">CONTACT &amp; CONTRIBUTE</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-900 md:text-3xl">
                投稿爆料 · 联系我们
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                如果你手头有压箱底的老报道、老照片，或亲历过某段当年的八卦旧事，
                欢迎把线索交给我们。一经核验采用，我们会与您联系并标注来源。
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a href={`mailto:${siteMeta.email}`} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700"><i className="ri-mail-line"></i></span>
                  <div>
                    <p className="text-xs text-foreground-500">投稿邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{siteMeta.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700"><i className="ri-time-line"></i></span>
                  <div>
                    <p className="text-xs text-foreground-500">人工核验周期</p>
                    <p className="text-sm font-medium text-foreground-900">收到后 3–5 个工作日内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                data-readdy-form="tougao-baoliao"
                onSubmit={onSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
              >
                <h3 className="font-display text-lg font-bold text-foreground-900">提交一条旧瓜线索</h3>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm text-foreground-700">你的称呼</label>
                    <input id="name" name="name" type="text" required placeholder="如何称呼你" className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm text-foreground-700">联系邮箱</label>
                    <input id="email" name="email" type="email" required placeholder="用于反馈核验结果" className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-1.5">
                  <label htmlFor="topic" className="text-sm text-foreground-700">投稿类型</label>
                  <select id="topic" name="topic" value={topic} onChange={(e) => setTopic(e.target.value)} className="cursor-pointer rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200">
                    <option>考古线索补充</option>
                    <option>历史爆料投稿</option>
                    <option>陈年旧瓜自述</option>
                    <option>合作与媒体</option>
                    <option>其他咨询</option>
                  </select>
                </div>

                <div className="mt-4 flex flex-col gap-1.5">
                  <label htmlFor="title" className="text-sm text-foreground-700">线索标题 <span className="text-foreground-400">(可选)</span></label>
                  <input id="title" name="title" type="text" placeholder="一句话概括你要爆的瓜" className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                </div>

                <div className="mt-4 flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-sm text-foreground-700">线索详情 <span className="text-foreground-400">(需附来源或见闻)</span></label>
                  <textarea id="message" name="message" rows={5} required maxLength={500} placeholder="请尽量写清时间、人物背景、信息来源，以及你认为值得重读的细节……" className="resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"></textarea>
                  <p className="text-right text-xs text-foreground-400">上限 500 字</p>
                </div>

                {/* honeypot：反垃圾隐蔽字段 */}
                <div className="tougao-field-guard" aria-hidden="true">
                  <label htmlFor="website_alt">网站</label>
                  <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                </div>

                {formError && (
                  <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100/60 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line"></i>{formError}
                  </p>
                )}
                {formState === 'success' && (
                  <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100/60 px-4 py-3 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line"></i>已收到你的投稿，感谢为档案添砖加瓦！我们会在核验后尽快联系你。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formState === 'sending'}
                  className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {formState === 'sending' ? (
                    <><i className="ri-loader-4-line animate-spin"></i>提交中…</>
                  ) : (
                    <><i className="ri-send-plane-line"></i>提交投稿</>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}